import type { ReactNode } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { Container, MeshTexture, RuleColumns, SideRail } from './primitives'

/**
 * The masthead every inner page opens with. It carries its own clearance under
 * the fixed header, so pages never have to know how tall the header is.
 */
export function PageHero({
  kicker,
  title,
  subtitle,
  rail,
  image,
  children,
  className,
}: {
  kicker?: string
  title: string
  subtitle?: string
  rail?: string
  image?: string
  children?: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'border-hairline bg-ink relative overflow-hidden border-b pt-[calc(var(--header-h)+2.5rem)] pb-14 md:pt-[calc(var(--header-h)+4rem)] md:pb-20',
        className
      )}
    >
      {image ? (
        <>
          <img
            src={image}
            alt=""
            className="absolute inset-0 size-full object-cover opacity-30"
            loading="eager"
          />
          <div className="from-ink via-ink/70 to-ink/40 absolute inset-0 bg-gradient-to-t" />
        </>
      ) : (
        <>
          <MeshTexture opacity={0.05} />
          <RuleColumns />
        </>
      )}

      {rail ? <SideRail label={rail} /> : null}

      <Container className={cn('relative', rail && 'lg:pl-14')}>
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {kicker ? (
            <div className="mb-5 flex items-center gap-3">
              <span className="bg-blood h-px w-10" aria-hidden />
              <span className="eyebrow text-blood">{kicker}</span>
            </div>
          ) : null}

          <h1 className="display text-balance-safe text-bone text-[clamp(2.75rem,10vw,7.5rem)] font-bold">
            {title}
          </h1>

          {subtitle ? (
            <p className="text-bone-dim mt-6 max-w-2xl text-[1.0625rem] leading-relaxed md:text-lg">
              {subtitle}
            </p>
          ) : null}

          {children ? <div className="mt-8">{children}</div> : null}
        </motion.div>
      </Container>
    </section>
  )
}
